import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, CalendarDays, Receipt, Wallet, LogOut, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusTone = (s) => {
  const v = (s || "").toLowerCase();
  if (v.includes("paid") || v.includes("approved") || v.includes("complete")) return "border-emerald-500/40 text-emerald-300";
  if (v.includes("pending") || v.includes("await")) return "border-amber-500/40 text-amber-300";
  return "border-slate-600 text-slate-300";
};

export default function SecondParentFamilyView({ user, logout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/academy/family/overview`);
        setData(res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not load your family view");
      }
    };
    load();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-red-500/30 bg-red-500/5">
          <CardContent className="pt-6 text-center space-y-4">
            <p className="text-red-200" data-testid="family-view-error">{error}</p>
            <Button variant="outline" onClick={logout} data-testid="family-logout-error-btn">
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="second-parent-family-view">
      <div className="max-w-5xl mx-auto px-5 py-10 space-y-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-sky-400/80">Football And More</p>
            <h1 className="text-3xl sm:text-4xl font-bold mt-1">Family view</h1>
            <p className="text-sm text-slate-400 mt-2">
              Signed in as {data.viewer?.name || data.viewer?.email} · second parent on{" "}
              <span className="text-slate-200">{data.primary_parent?.name}</span>&apos;s account
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="family-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
        </header>

        <Card className="border-sky-500/20 bg-sky-500/5">
          <CardContent className="pt-5 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-sky-300 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-300">
              This is a read-only view. Registrations, payments and account changes stay with{" "}
              {data.primary_parent?.name || "the primary parent"} ({data.primary_parent?.email}).
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Users className="w-5 h-5 text-sky-400" /> Children
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.children?.length === 0 && (
              <p className="text-sm text-slate-400" data-testid="family-no-children">No children on this account yet.</p>
            )}
            {data.children?.map((c) => (
              <div key={c.id} className="rounded-lg border border-slate-800 bg-slate-950/60 p-4" data-testid={`family-child-${c.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-white">{c.name}</p>
                  {c.age_group && <Badge variant="outline" className="border-slate-700 text-slate-300">{c.age_group}</Badge>}
                  {c.teams?.map((t) => (
                    <Badge key={t} variant="outline" className="border-sky-500/30 text-sky-200">{t}</Badge>
                  ))}
                </div>
                <div className="mt-3 space-y-1">
                  {c.sessions?.length === 0 && (
                    <p className="text-xs text-slate-500">No sessions booked right now.</p>
                  )}
                  {c.sessions?.map((s, i) => (
                    <p key={i} className="text-sm text-slate-300 flex items-center gap-2">
                      <CalendarDays className="w-3.5 h-3.5 text-slate-500" />
                      {s.day} {s.start_time}
                      {s.end_time ? `–${s.end_time}` : ""} · {s.name}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Receipt className="w-5 h-5 text-sky-400" /> Invoices &amp; payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.invoices?.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="family-no-invoices">Nothing invoiced yet.</p>
            ) : (
              <div className="divide-y divide-slate-800">
                {data.invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between py-3" data-testid={`family-invoice-${inv.id}`}>
                    <div>
                      <p className="text-sm text-white">{inv.term_name || "Registration"}</p>
                      <p className="text-xs text-slate-500">{inv.reference || inv.id?.slice(0, 8)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={statusTone(inv.status)}>{inv.status || "—"}</Badge>
                      <span className="font-mono text-sm text-slate-200">${inv.amount?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {data.credit?.available > 0 && (
          <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="family-credit-card">
            <CardContent className="pt-5 flex items-start gap-3">
              <Wallet className="w-5 h-5 text-emerald-300 mt-0.5" />
              <div>
                <p className="text-white font-semibold">
                  ${data.credit.available.toFixed(2)} account credit available
                </p>
                <p className="text-sm text-slate-300">
                  {data.credit.notes?.length > 0
                    ? `From ${data.credit.notes.join(", ")}. It is applied automatically to the next invoice.`
                    : "Applied automatically to the next invoice."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
